import { Box, Link, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { wordpressService, type WordPressPost } from "@/services/wordpress";
import { parseHtmlString } from "@/utils";

interface PostCarouselProps {
    posts: WordPressPost[];
}

function Placeholder() {
    const { t } = useTranslation();
    return (
        <Box sx={{ textAlign: "center", color: "white" }}>
            <Typography component="h2" variant="h3" sx={{ fontWeight: 600 }}>
                {t("placeholder-heading")}
            </Typography>
            <Typography sx={{ opacity: 0.9, mt: 1 }}>
                {t("placeholder-tagline")}
            </Typography>
        </Box>
    );
}

function PostCarouselItem({ post }: { post: WordPressPost }) {
    const { t } = useTranslation();

    // Truncate excerpt to ~100 characters and remove HTML tags
    function truncateExcerpt(html: string, maxLength: number = 100) {
        const text = parseHtmlString(html).trim();
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + "...";
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                maxWidth: 500,
                mt: 10,
                mx: "auto",
                px: 3,
            }}
        >
            <Typography
                component="h2"
                variant="h3"
                sx={{
                    color: "white",
                    fontWeight: 600,
                    lineHeight: 1.2,
                }}
            >
                {parseHtmlString(post.title.rendered)}
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    textAlign: "center",
                    color: "white",
                    opacity: 0.9,
                    lineHeight: 1.5,
                    mb: 1,
                }}
            >
                {truncateExcerpt(post.excerpt.rendered)}
            </Typography>

            <Link
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    color: "white",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                        textDecorationColor: "white",
                        opacity: 0.9,
                    },
                    fontWeight: 500,
                }}
            >
                {t("read-more")} →
            </Link>
        </Box>
    );
}

export function PostCarousel({ posts }: PostCarouselProps) {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (posts.length === 0) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % posts.length);
        }, 12000); // Change slide every 12 seconds

        return () => clearInterval(interval);
    }, [posts.length]);

    if (posts.length === 0) {
        return <Placeholder />;
    }

    const currentPost = posts[currentIndex];

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            <PostCarouselItem post={currentPost} />

            {/* Navigation dots */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1.5,
                    mt: 4,
                }}
            >
                {posts.map((_, index) => (
                    <Box
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor:
                                index === currentIndex
                                    ? "white"
                                    : "rgba(255, 255, 255, 0.4)",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                backgroundColor:
                                    index === currentIndex
                                        ? "white"
                                        : "rgba(255, 255, 255, 0.6)",
                                transform: "scale(1.1)",
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

export function LatestPostsCarousel() {
    const { t } = useTranslation();
    const [posts, setPosts] = useState<WordPressPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchPosts() {
            try {
                const allPosts = await wordpressService.getLatestPosts(10);
                setPosts(allPosts.slice(0, 3)); // Take only the latest 3
            } catch (err) {
                console.error("Failed to fetch WordPress posts:", err);
                setError("Failed to load latest posts");
            } finally {
                setLoading(false);
            }
        }

        fetchPosts();
    }, []);

    if (loading) {
        return (
            <Box sx={{ textAlign: "center", color: "white" }}>
                <Typography
                    component="h2"
                    variant="h3"
                    sx={{ fontWeight: 600 }}
                >
                    {t("placeholder-heading")}
                </Typography>
                <Typography sx={{ opacity: 0.9, mt: 1 }}>
                    {t("login.loading-posts")}
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <Placeholder />;
    }

    return <PostCarousel posts={posts} />;
}
